import React from 'react';
import AppRouter from 'common/AppRouter';
import { PM_AUTH_STATES, PROVIDERS_MAP } from 'common/constants/constants';
import { getManagementTool } from 'common/utils/utils';
import { Padding, WhiteSection } from 'components/base/layout-components';
import QueryString from 'query-string';
import { Redirect, Route, Switch } from 'react-router-dom';
import Tabs from './components/Tabs';
import AuthState from './components/AuthState';
import Jira from './views/Jira';
import Clubhouse from './views/Clubhouse';
import AzureBoards from './views/AzureBoards/AzureBoards';
import Cards from './components/Cards';
import useSettingsService from 'components/hooks/useSettingsService';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import { MigeratioStaleEmptyState } from './components/MigrationStaleEmptyState';
import { isSensorOAuth2 } from 'common/services/sensors.service';
import useSensorReauthHandler from 'common/hooks/auth/useSensorReauthHandler';
import MultiSitesErrorModal from 'modals/MultiSitesErrorModal';

const mapPmToolProviderPathToComponent = {
  [PROVIDERS_MAP.jira.provider_path]: Jira,
  [PROVIDERS_MAP.clubhouse.provider_path]: Clubhouse,
  [PROVIDERS_MAP.azure_boards.provider_path]: AzureBoards
};

const ProjectManagement = ({
  sensorAuthorizations,
  history,
  toggleSnackBar,
  selectedTeam,
  isOrgSettings,
  managementToolView,
  getSensorAuthorizations,
  toggleModal,
  apiTokens,
  setApiTokens
}) => {
  const { getSettingOrDefinitionValue } = useSettingsService();
  const connectedManagementTool = getManagementTool({
    sensorAuthorizations
  });
  const isOAuth2 = isSensorOAuth2(connectedManagementTool);

  const isAuthorized =
    !!connectedManagementTool &&
    (connectedManagementTool.state !== PM_AUTH_STATES.IN_PROGRESS || isOAuth2);

  const isShowExpediaMigrationStaleMode = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.showExpediaMigrationStaleMode
  });
  const reauthHandler = useSensorReauthHandler(connectedManagementTool);

  if (isShowExpediaMigrationStaleMode) {
    return (
      <WhiteSection>
        <MigeratioStaleEmptyState />
      </WhiteSection>
    );
  }

  return (
    <WhiteSection>
      <Switch>
        <Route
          path={AppRouter.PROJECT_MANAGEMENT.path}
          exact
          render={() => {
            const redirectTo = isAuthorized
              ? `${
                  AppRouter.PROJECT_MANAGEMENT.path
                }/${connectedManagementTool.sensor_name.toLowerCase()}`
              : `${AppRouter.PROJECT_MANAGEMENT_PROJECTS.path}`;

            return (
              <Redirect
                to={{
                  pathname: redirectTo,
                  search: isOrgSettings ? 'isOrgSettings=true' : ''
                }}
              />
            );
          }}
        />

        <Route
          path={AppRouter.PROJECT_MANAGEMENT_TOOLS.path}
          render={({
            match: {
              params: { managementTool }
            }
          }) => {
            const tool = QueryString.parseUrl(managementTool).url;
            const PmComponent = mapPmToolProviderPathToComponent[tool];

            if (managementTool !== 'projects' && !PmComponent) return null;

            const pmProvider = PROVIDERS_MAP[managementTool];

            return (
              <>
                <Padding t='x-medium'>
                  {isAuthorized && pmProvider ? (
                    <AuthState
                      sensor={connectedManagementTool}
                      icon={pmProvider.logo}
                      name={pmProvider.name}
                      isOrgSettings={isOrgSettings}
                      onReauth={reauthHandler.onReauth}
                    />
                  ) : null}
                </Padding>

                <Tabs
                  toggleSnackBar={toggleSnackBar}
                  managementTool={managementTool}
                  selectedTeam={selectedTeam}
                  apiTokens={apiTokens}
                  setApiTokens={setApiTokens}
                  toggleModal={toggleModal}
                  isOrgSettings={isOrgSettings}
                  isAuthorized={isAuthorized}
                  {...(managementTool === 'projects'
                    ? {
                        getPmComponent: () => (
                          <Cards
                            isOrgSettings={isOrgSettings}
                            history={history}
                            connectedManagementTool={connectedManagementTool}
                            isAuthorized={isAuthorized}
                          />
                        )
                      }
                    : {
                        getPmComponent: entityName => (
                          <PmComponent
                            entityName={entityName}
                            selectedTeam={selectedTeam}
                            isOrgSettings={isOrgSettings}
                            apiTokens={apiTokens}
                            setApiTokens={setApiTokens}
                          />
                        )
                      })}
                />
              </>
            );
          }}
        />
      </Switch>
      <MultiSitesErrorModal sensorName={managementToolView} />
    </WhiteSection>
  );
};

export default ProjectManagement;
